/* Bridal PDP and collection quick add. Reuses the theme cart form and drawer. */
(() => {
  if (window.kipperBrideInitialized) return;
  const bridalTemplate = document.body.classList.contains('kipper-bride-template');
  if (!bridalTemplate && !document.querySelector('[data-bride-quick-products]')) return;
  window.kipperBrideInitialized = true;

  const currency = window.Shopify?.currency?.active || 'GBP';
  const money = cents => new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
    style: 'currency', currency
  }).format(Number(cents || 0) / 100);
  const addUrl = () => `${window.KROWN?.settings?.routes?.cart_add_url || '/cart/add'}.js`;
  const cartUrl = () => `${window.Shopify?.routes?.root || '/'}cart.js`;
  const changeUrl = () => `${window.Shopify?.routes?.root || '/'}cart/change.js`;
  const defaultPreorderNote = 'This item is on pre-order and will ship when available.';
  const isPreorder = variant => variant?.preorder === true || variant?.dataset?.preorder === 'true';
  const preorderValue = (source, key) => source?.dataset?.[key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase())] || source?.[key] || '';
  function dispatchWeek(source) {
    const value = preorderValue(source, 'preorder_dispatch_week');
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return '';
    const date = new Date(`${value}T00:00:00Z`);
    if (!Number.isFinite(date.getTime()) || date.toISOString().slice(0, 10) !== value) return '';
    return new Intl.DateTimeFormat(document.documentElement.lang || 'en-GB', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }).format(date);
  }
  function preorderMessages(sources, fallback = defaultPreorderNote) {
    return [...new Set(sources.filter(isPreorder).map(source => {
      const date = dispatchWeek(source);
      const note = preorderValue(source, 'preorder_note') || fallback;
      const label = preorderValue(source, 'preorder_label');
      const message = date
        ? `${note && note !== defaultPreorderNote ? `${note}\n` : ''}Pre-order — expected dispatch week commencing ${date}.`
        : note || defaultPreorderNote;
      return label ? `${label}: ${message}` : message;
    }))].join('\n');
  }
  function preorderProperties(variant) {
    if (!isPreorder(variant)) return {};
    const date = dispatchWeek(variant);
    return { 'Order type': 'Pre-order', ...(date ? { 'Expected dispatch': `Week commencing ${date}` } : {}) };
  }
  function bundlePreorderProperties(sources) {
    const properties = {};
    sources.filter(isPreorder).forEach(source => {
      properties['Order type'] = 'Pre-order';
      const date = dispatchWeek(source);
      if (date) properties[preorderValue(source, 'preorder_label') ? `${preorderValue(source, 'preorder_label')} expected dispatch` : 'Expected dispatch'] = `Week commencing ${date}`;
    });
    return properties;
  }
  const preorderDataRequests = new Map();
  function loadPreorderData(handle, signal) {
    if (preorderDataRequests.has(handle)) return preorderDataRequests.get(handle);
    const pending = (async () => {
      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}products/${encodeURIComponent(handle)}?section_id=bride-preorder-data`, { signal, headers: { Accept: 'text/html' } });
        if (!response.ok) return null;
        const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
        const data = JSON.parse(documentFragment.querySelector('[data-bride-preorder-data]')?.textContent || 'null');
        return data?.product_id ? data : null;
      } catch (_) { return null; }
    })();
    preorderDataRequests.set(handle, pending);
    pending.then(data => { if (!data) preorderDataRequests.delete(handle); });
    return pending;
  }
  const quickDispatchRequests = new Map();
  function quickPreorderSources(variant, context) {
    if (!isPreorder(variant)) return Promise.resolve([]);
    const fallback = { ...variant, preorder_dispatch_week: preorderValue(variant, 'preorder_dispatch_week') || preorderValue(context, 'preorder_dispatch_week'), preorder_note: preorderValue(context, 'preorder_note') };
    if (dispatchWeek(fallback) && (variant.options?.length || 1) === 1) return Promise.resolve([fallback]);
    const country = /^[A-Z]{2}$/.test(context?.dataset?.bundleCountry || '') ? context.dataset.bundleCountry : null;
    const key = `${variant.id}:${country || ''}`;
    if (quickDispatchRequests.has(key)) return quickDispatchRequests.get(key);
    const pending = (async () => {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 8000);
      try {
        const response = await fetch('/api/2026-10/graphql.json', {
          method: 'POST', signal: controller.signal, headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ query: `query BridePreorderDates($id: ID!, $country: CountryCode) @inContext(country: $country) {
            node(id: $id) { ... on ProductVariant { requiresComponents components(first: 3) { nodes { quantity productVariant {
              availableForSale currentlyNotInStock product { id title handle }
            } } } } }
          }`, variables: { id: `gid://shopify/ProductVariant/${variant.id}`, country } })
        });
        const result = await response.json();
        const node = result.data?.node;
        if (!response.ok || result.errors?.length || !node?.requiresComponents || node.components.nodes.length !== 2 || node.components.nodes.some(component => component.quantity !== 1)) return [fallback];
        const sources = await Promise.all(node.components.nodes.map(async component => {
          const child = component.productVariant;
          if (!child.availableForSale || !child.currentlyNotInStock) return null;
          const metadata = await loadPreorderData(child.product.handle, controller.signal);
          if (!metadata || String(metadata.product_id) !== child.product.id.split('/').pop()) return null;
          return { ...metadata, preorder: true, preorder_label: /blazer|jacket/i.test(child.product.title) ? 'Blazer' : /trouser|pant/i.test(child.product.title) ? 'Trousers' : child.product.title };
        }));
        const known = sources.filter(Boolean);
        return known.length && (known.some(source => dispatchWeek(source)) || !dispatchWeek(fallback)) ? known : [fallback];
      } catch (_) { return [fallback]; }
      finally { clearTimeout(timeout); }
    })();
    quickDispatchRequests.set(key, pending);
    return pending;
  }
  const showError = (element, message) => {
    if (!element) return;
    element.textContent = message;
    element.hidden = !message;
  };

  async function refreshBrideCart(open = false) {
    const response = await fetch('?section_id=helper-cart', { cache: 'no-store' });
    if (!response.ok) throw new Error('Could not refresh your cart.');
    const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
    const form = document.getElementById('AjaxCartForm');
    const subtotal = document.getElementById('AjaxCartSubtotal');
    const nextForm = documentFragment.getElementById('AjaxCartForm');
    const nextSubtotal = documentFragment.getElementById('AjaxCartSubtotal');
    if (!form || !subtotal || !nextForm || !nextSubtotal) throw new Error('Could not refresh your cart.');
    form.innerHTML = nextForm.innerHTML;
    form.ajaxifyCartItems?.();
    subtotal.innerHTML = nextSubtotal.innerHTML;
    const itemCount = form.querySelector('[data-cart-count]')?.textContent?.trim() || '0';
    document.querySelectorAll('[data-header-cart-count], [data-bride-cart-count]').forEach(element => { element.textContent = itemCount; });
    const cartTotal = form.querySelector('[data-cart-total]')?.textContent;
    if (cartTotal) document.querySelectorAll('[data-header-cart-total]').forEach(element => { element.textContent = cartTotal; });
    form.dispatchEvent(new Event('cart-updated'));
    document.dispatchEvent(new CustomEvent('bride:cart-updated'));
    if (open) document.getElementById('site-cart-sidebar')?.show();
  }

  async function addItems(items, celebrate = false) {
    const response = await fetch(addUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items })
    });
    const result = await response.json();
    if (!response.ok) throw new Error(result.description || result.message || 'Could not add to cart.');
    if (document.getElementById('AjaxCartForm')) {
      try {
        await refreshBrideCart(true);
      } catch (_) {
        window.location.href = window.KROWN?.settings?.routes?.cart_url || '/cart';
        return result;
      }
      const banner = document.querySelector('[data-bride-cart-added]');
      if (banner) banner.hidden = !celebrate;
    } else {
      window.location.href = window.KROWN?.settings?.routes?.cart_url || '/cart';
    }
    return result;
  }

  async function changeItem(key, quantity) {
    const response = await fetch(changeUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: String(key), quantity })
    });
    const result = await response.json();
    if (!response.ok) throw new Error(result.description || result.message || 'Could not update your cart.');
    const banner = document.querySelector('[data-bride-cart-added]');
    if (banner) banner.hidden = true;
    try {
      await refreshBrideCart(false);
    } catch (_) {
      window.location.href = window.KROWN?.settings?.routes?.cart_url || '/cart';
    }
    return result;
  }

  function initProduct(section) {
    if (section.dataset.brideReady) return;
    section.dataset.brideReady = 'true';
    const form = section.querySelector('[data-bride-product-form]');
    const error = section.querySelector('[data-bride-error]');
    const addButton = section.querySelector('[data-bride-add]');
    const sticky = section.querySelector('[data-bride-sticky]');
    const stickyButton = section.querySelector('[data-bride-sticky-add]');
    let purchaseDrawer;
    const info = section.querySelector('.bride-product__info');
    const customization = section.querySelector('.bride-product__custom');
    const lengthToggle = section.querySelector('[data-bride-unique-length]');
    const lengthPanel = section.querySelector('[data-bride-length-panel]');
    const lengthInput = section.querySelector('[data-bride-inside-leg]');
    const pair = section.dataset.pair === 'true';
    const native = section.dataset.nativeOptions === 'true';
    const separateSizes = pair || native;
    const variants = native ? JSON.parse(section.querySelector('[data-bride-variants]').textContent) : [];
    const blazerIndex = Number(section.dataset.blazerOptionIndex);
    const trouserIndex = Number(section.dataset.trouserOptionIndex);
    const variantId = section.querySelector('[data-bride-variant-id]');
    const initialPrice = section.querySelector('[data-bride-price]').textContent;
    const addPrice = addButton.querySelector('[data-bride-add-price]');
    const addPriceValue = addButton.querySelector('[data-bride-add-price-value]');
    const stockNotice = section.querySelector('[data-bride-stock]');
    const addLabel = addButton.querySelector('[data-bride-add-label]');
    const normalAddLabel = addLabel?.textContent || stickyButton.dataset.addLabel;
    const preorderNotice = section.querySelector('[data-bride-preorder-note]');
    let selectedPreorder = false;
    function updateAddLabels() {
      if (addLabel) addLabel.textContent = selectedPreorder ? 'Pre-order' : normalAddLabel;
      stickyButton.textContent = !purchaseDrawer?.isOpen() && sizeGroups().some(name => !checked(name))
        ? (purchaseMode() === 'suit' ? stickyButton.dataset.chooseLabel : 'Choose size')
        : selectedPreorder ? 'Pre-order' : stickyButton.dataset.addLabel;
    }
    // Match the selected option values just as the theme's ProductVariants component does.
    const matchSuitVariant = (blazer, trouser) => variants.find(variant =>
      variant.options[blazerIndex] === blazer && variant.options[trouserIndex] === trouser);
    const suitPreorders = (blazer, trouser, variant) => {
      if (variant?.available && section.brideBundlePieces) {
        const children = ['blazer', 'trouser'].map((mode, index) => {
          const product = section.brideBundlePieces.get(mode);
          const value = [blazer, trouser][index]?.value;
          const normalize = size => /^\d+$/.test(String(size)) ? String(Number(size)) : String(size);
          const child = product?.variants.find(item => item.options.length === 1 && normalize(item.options[0]) === normalize(value));
          return child && isPreorder(child) ? { ...child, preorder_label: mode === 'blazer' ? 'Blazer' : 'Trousers' } : null;
        }).filter(Boolean);
        if (children.length) return children.some(child => dispatchWeek(child)) || !dispatchWeek(variant) ? children : [{ ...variant, preorder: true }];
      }
      return variant ? [variant] : [];
    };
    section.brideSuitPreorders = suitPreorders;
    const heroPrice = document.querySelector('[data-bride-hero-price]');
    if (heroPrice) heroPrice.textContent = section.querySelector('[data-bride-price]').textContent;

    const checked = name => form.querySelector(`input[name="${name}"]:checked`);
    const purchaseChoice = () => checked('bride_purchase');
    const purchaseMode = () => purchaseChoice()?.value || 'suit';
    const sizeGroups = () => purchaseMode() === 'suit'
      ? (separateSizes ? ['bride_blazer', 'bride_trouser'] : ['bride_variant'])
      : [`bride_${purchaseMode()}_only`];
    const stickyTitle = section.querySelector('[data-bride-sticky-title]');
    const stickyImage = section.querySelector('.bride-sticky__product img');
    const initialTitle = stickyTitle?.textContent;
    const initialImage = stickyImage ? { src: stickyImage.getAttribute('src'), srcset: stickyImage.getAttribute('srcset') } : null;
    const selectedBride = () => section.querySelector('input[name="bride_colour"]:checked')?.value || '';
    // The name attribute handles current browsers; toggle also supports older Safari.
    const accordions = [...section.querySelectorAll('.bride-product__accordions > details')];
    accordions.forEach(details => details.addEventListener('toggle', () => {
      if (details.open) accordions.forEach(other => { if (other !== details) other.open = false; });
    }));
    const fitStickyInfo = () => {
      if (!info || window.innerWidth <= 1023) return;
      const header = document.querySelector('.mount-header');
      const headerBottom = Math.max(84, Math.ceil(header?.getBoundingClientRect().height || 0) + 12);
      info.style.setProperty('--bride-info-sticky-top', `${Math.min(headerBottom, window.innerHeight - info.offsetHeight - 20)}px`);
    };
    const infoResize = new ResizeObserver(fitStickyInfo);
    if (info) infoResize.observe(info);
    window.addEventListener('resize', fitStickyInfo);
    fitStickyInfo();
    function updateSizeLabels(showMissing = false) {
      const groups = sizeGroups();
      groups.forEach((name, index) => {
        const fieldset = form.querySelector(`input[name="${name}"]`)?.closest('fieldset');
        const label = fieldset?.querySelector('[data-bride-size-label]');
        if (!fieldset || !label) return;
        const selected = checked(name);
        const selectedText = selected?.nextElementSibling?.textContent?.trim();
        const missing = showMissing && !selected;
        label.textContent = selectedText ? `${label.dataset.label} — ${selectedText}`
          : missing ? (purchaseMode() === 'suit' && separateSizes ? (index ? 'And your trouser size ↓' : 'Pick your blazer size first ↓') : 'Pick your size first ↓')
          : label.dataset.label;
        fieldset.classList.toggle('is-error', missing);
      });
    }
    function updateNativeAvailability() {
      form.querySelectorAll('[name="bride_blazer"]').forEach(input => {
        input.disabled = !variants.some(variant => variant.available && variant.options[blazerIndex] === input.value);
        if (input.disabled) input.checked = false;
      });
      const blazer = checked('bride_blazer');
      form.querySelectorAll('[name="bride_trouser"]').forEach(input => {
        input.disabled = !variants.some(variant => variant.available && variant.options[trouserIndex] === input.value
          && (!blazer || variant.options[blazerIndex] === blazer.value));
        if (input.disabled) input.checked = false;
      });
    }
    function updateSelection() {
      const mode = purchaseMode();
      const choice = purchaseChoice();
      const wholeSuit = mode === 'suit';
      const suitOptions = section.querySelector('[data-bride-suit-options]');
      if (suitOptions) suitOptions.hidden = !wholeSuit;
      section.querySelectorAll('[data-bride-piece-options]').forEach(group => { group.hidden = group.dataset.bridePieceOptions !== mode; });
      section.querySelectorAll('[data-bride-custom-for]').forEach(group => { group.hidden = !wholeSuit && group.dataset.brideCustomFor !== mode; });
      const lengthOptions = lengthToggle?.closest('.bride-product__unique-length');
      if (lengthOptions) lengthOptions.hidden = mode === 'blazer';
      if (lengthInput) lengthInput.required = mode !== 'blazer' && Boolean(lengthToggle?.checked);
      form.querySelectorAll('.bride-product__sizes.is-error').forEach(group => group.classList.remove('is-error'));
      if (native && wholeSuit) updateNativeAvailability();
      const blazer = checked('bride_blazer');
      const trouser = checked('bride_trouser');
      const variant = checked('bride_variant');
      const piece = wholeSuit ? null : checked(`bride_${mode}_only`);
      const suitVariant = native && blazer && trouser ? matchSuitVariant(blazer.value, trouser.value) : null;
      const selections = !wholeSuit ? [piece] : native ? suitPreorders(blazer, trouser, suitVariant) : pair ? [blazer, trouser] : [variant];
      selectedPreorder = selections.some(isPreorder);
      if (preorderNotice) {
        preorderNotice.textContent = preorderMessages(selections, section.dataset.preorderNote || defaultPreorderNote);
        preorderNotice.hidden = !selectedPreorder;
      }
      if (variantId) {
        variantId.value = wholeSuit && suitVariant?.available ? suitVariant.id : '';
        variantId.disabled = !wholeSuit || !suitVariant?.available;
      }
      const price = !wholeSuit ? (piece ? Number(piece.dataset.price) : null) : native ? (suitVariant?.available ? Number(suitVariant.price) : null) : pair
        ? (blazer && trouser ? Number(blazer.dataset.price) + Number(trouser.dataset.price) : null)
        : (variant ? Number(variant.dataset.price) : null);
      const displayPrice = price !== null ? money(price) : wholeSuit ? initialPrice : money(Number(choice.dataset.price));
      section.querySelector('[data-bride-price]').textContent = displayPrice;
      section.querySelector('[data-bride-sticky-price]').textContent = displayPrice;
      if (heroPrice) heroPrice.textContent = wholeSuit ? displayPrice : initialPrice;
      if (addPriceValue) addPriceValue.textContent = displayPrice;
      if (stickyTitle) stickyTitle.textContent = wholeSuit ? initialTitle : choice.dataset.title;
      if (stickyImage && initialImage) {
        if (!wholeSuit && choice.dataset.image) {
          stickyImage.src = choice.dataset.image;
          stickyImage.removeAttribute('srcset');
        } else {
          stickyImage.setAttribute('src', initialImage.src);
          if (initialImage.srcset) stickyImage.setAttribute('srcset', initialImage.srcset);
          else stickyImage.removeAttribute('srcset');
        }
      }
      const summary = !wholeSuit ? (piece ? `${mode === 'blazer' ? 'Blazer' : 'Trousers'} ${piece.nextElementSibling.textContent.trim()}` : `Pick your ${mode === 'blazer' ? 'blazer' : 'trouser'} size`) : separateSizes
        ? (blazer && trouser ? `Blazer ${blazer.nextElementSibling.textContent.trim()} · Trousers ${trouser.nextElementSibling.textContent.trim()}` : 'Pick your sizes')
        : (variant ? variant.nextElementSibling.textContent.trim() : 'Pick your sizes');
      section.querySelector('[data-bride-sticky-selection]').textContent = summary;
      updateAddLabels();
      if (addPrice) addPrice.hidden = price === null;
      if (stockNotice) {
        const limited = !wholeSuit ? (piece?.dataset.inventory !== undefined && Number(piece.dataset.inventory) > 0 && Number(piece.dataset.inventory) <= 2 ? { input: piece, piece: mode === 'blazer' ? 'blazer' : 'trousers' } : null) : pair ? [
          { input: blazer, piece: 'blazer' }, { input: trouser, piece: 'trouser' }
        ].find(({ input }) => input?.dataset.inventory !== undefined && Number(input.dataset.inventory) > 0 && Number(input.dataset.inventory) <= 2)
          : native && suitVariant?.low_stock > 0
            ? { input: { dataset: { inventory: suitVariant.low_stock }, nextElementSibling: { textContent: '' } }, piece: 'this combination' }
            : null;
        stockNotice.textContent = limited ? `Only ${limited.input.dataset.inventory} left in ${limited.piece}${pair || !wholeSuit ? ` size ${limited.input.nextElementSibling.textContent.trim()}` : ''}` : '';
        stockNotice.hidden = !limited;
      }
      updateSizeLabels();
      showError(error, '');
    }
    form.addEventListener('change', event => {
      if (lengthToggle && event.target === lengthToggle) {
        lengthPanel.hidden = !lengthToggle.checked;
        lengthInput.required = lengthToggle.checked;
        if (!lengthToggle.checked) lengthInput.value = '';
      }
      updateSelection();
    });
    lengthInput?.addEventListener('input', () => {
      if (lengthInput.value && lengthInput.checkValidity()) showError(error, '');
    });
    form.addEventListener('submit', async event => {
      event.preventDefault();
      if (addButton.disabled) return;
      const blazer = checked('bride_blazer');
      const trouser = checked('bride_trouser');
      const variant = checked('bride_variant');
      const mode = purchaseMode();
      const wholeSuit = mode === 'suit';
      const piece = wholeSuit ? null : checked(`bride_${mode}_only`);
      if (!wholeSuit && section.dataset.brideBundleState === 'loading') {
        showError(error, 'Loading bundle pieces. Please try again.');
        return;
      }
      if (!wholeSuit && (!piece || piece.disabled)) {
        updateSizeLabels(true);
        showError(error, `Choose your ${mode === 'blazer' ? 'blazer' : 'trouser'} size.`);
        form.querySelector(`[name="bride_${mode}_only"]:not(:disabled)`)?.focus();
        return;
      }
      if (wholeSuit && separateSizes && (!blazer || !trouser)) {
        updateSizeLabels(true);
        showError(error, 'Choose both a blazer size and a trouser size.');
        (!blazer ? form.querySelector('[name="bride_blazer"]') : form.querySelector('[name="bride_trouser"]'))?.focus();
        return;
      }
      if (wholeSuit && !separateSizes && !variant) {
        updateSizeLabels(true);
        showError(error, 'Choose your size.');
        form.querySelector('[name="bride_variant"]')?.focus();
        return;
      }
      const suitVariant = wholeSuit && native ? matchSuitVariant(blazer.value, trouser.value) : null;
      if (wholeSuit && native && !suitVariant?.available) {
        showError(error, 'This size combination is unavailable. Choose another blazer or trouser size.');
        return;
      }
      if (mode !== 'blazer' && lengthToggle?.checked && (!lengthInput.value || !lengthInput.checkValidity())) {
        showError(error, 'Enter a valid inside-leg length in centimetres.');
        lengthInput.focus();
        return;
      }
      const pockets = checked('bride_pockets')?.value || 'Clean front';
      const loops = checked('bride_loops')?.value || 'Without';
      const length = mode !== 'blazer' && lengthToggle?.checked ? lengthInput.value : '';
      const bride = selectedBride();
      const brideProperty = bride ? { 'Which bride are you?': bride } : {};
      const purchaseProperty = purchaseChoice() ? { 'Purchase option': purchaseChoice().dataset.label } : {};
      const items = !wholeSuit ? [
        { id: Number(piece.value), quantity: 1, properties: { ...brideProperty, ...purchaseProperty, ...preorderProperties(piece), ...(customization ? (mode === 'blazer' ? { Pockets: pockets } : { 'Belt loops': loops }) : {}), ...(length ? { 'Inside leg (cm)': length } : {}) } }
      ] : pair ? [
        { id: Number(blazer.value), quantity: 1, properties: { ...brideProperty, ...purchaseProperty, ...preorderProperties(blazer), ...(customization ? { Pockets: pockets } : {}), '_Bridal suit': section.querySelector('h2')?.textContent?.trim() || '' } },
        { id: Number(trouser.value), quantity: 1, properties: { ...brideProperty, ...purchaseProperty, ...preorderProperties(trouser), ...(customization ? { 'Belt loops': loops } : {}), ...(length ? { 'Inside leg (cm)': length } : {}), '_Bridal suit': section.querySelector('h2')?.textContent?.trim() || '' } }
      ] : [
        { id: native ? Number(suitVariant.id) : Number(variant.value), quantity: 1, properties: { ...brideProperty, ...purchaseProperty, ...(native ? bundlePreorderProperties(suitPreorders(blazer, trouser, suitVariant)) : preorderProperties(variant)), ...(customization ? { Pockets: pockets, 'Belt loops': loops } : {}), ...(length ? { 'Inside leg (cm)': length } : {}) } }
      ];
      addButton.disabled = true;
      stickyButton.disabled = true;
      stickyButton.setAttribute('aria-busy', 'true');
      stickyButton.textContent = 'Adding…';
      showError(error, '');
      try {
        await addItems(items, true);
        purchaseDrawer?.close(false, true);
        document.dispatchEvent(new CustomEvent('bride:cart-added'));
      } catch (failure) {
        showError(error, failure.message);
        if (purchaseDrawer?.isOpen()) error?.scrollIntoView({ block: 'nearest' });
      } finally {
        addButton.disabled = false;
        stickyButton.disabled = false;
        stickyButton.removeAttribute('aria-busy');
        // Restore the label without clearing a cart error or changing selections.
        updateAddLabels();
      }
    });
    purchaseDrawer = initStickyPurchase(section, form, sticky, stickyButton, updateSelection);
    stickyButton.addEventListener('click', () => {
      if (purchaseDrawer) {
        if (purchaseDrawer.isOpen()) form.requestSubmit();
        else purchaseDrawer.open();
        return;
      }
      const missingName = sizeGroups().find(name => !checked(name));
      const missing = missingName ? form.querySelector(`[name="${missingName}"]:not(:disabled)`) : null;
      if (missing) {
        const target = section.querySelector('#buy') || missing.closest('fieldset') || missing;
        window.scrollTo({ top: target.getBoundingClientRect().top + window.scrollY - 80, behavior: 'smooth' });
        return;
      }
      form.requestSubmit();
    });
    const updateSticky = () => {
      const cartOpen = document.body.classList.contains('sidebar-opened');
      if (cartOpen) purchaseDrawer?.close(false, true);
      const button = addButton.getBoundingClientRect();
      const buttonVisible = button.bottom > 0 && button.top < window.innerHeight;
      const visible = (purchaseDrawer?.isOpen() || (window.scrollY > 700 && !buttonVisible)) && !cartOpen;
      sticky.classList.toggle('is-visible', visible);
      sticky.inert = !visible || Boolean(purchaseDrawer?.isOpen() && section.querySelector('[data-bride-size-guide].active'));
      sticky.setAttribute('aria-hidden', String(!visible));
    };
    sticky.hidden = false;
    window.addEventListener('scroll', updateSticky, { passive: true });
    window.addEventListener('resize', updateSticky);
    const stickyObserver = new MutationObserver(updateSticky);
    stickyObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    const beforeCartOpen = () => { if (addButton.disabled) purchaseDrawer?.close(false, true); };
    document.addEventListener('bride:cart-updated', beforeCartOpen);
    section.closest('.shopify-section')?.addEventListener('shopify:section:unload', () => {
      window.removeEventListener('scroll', updateSticky);
      window.removeEventListener('resize', updateSticky);
      stickyObserver.disconnect();
      document.removeEventListener('bride:cart-updated', beforeCartOpen);
      purchaseDrawer?.destroy();
      stopBundleLookup();
      infoResize.disconnect();
      window.removeEventListener('resize', fitStickyInfo);
    }, { once: true });
    updateSelection();
    updateSticky();
    initSizeGuide(section, form, native ? matchSuitVariant : null, sizeGroups);
    const stopBundleLookup = initBundleComponents(section, form, () => {
      const previousError = error?.textContent || '';
      updateSelection();
      showError(error, previousError);
    });
  }

  function initBundleComponents(section, form, updateSelection) {
    const configuration = form.querySelector('[data-bride-bundle-config]');
    const sizeTemplate = form.querySelector('[data-bride-bundle-sizes]');
    if (!configuration || !sizeTemplate) return () => {};
    let config;
    try { config = JSON.parse(configuration.textContent); } catch (_) { return () => {}; }
    if (!config.handle) return () => {};
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8000);
    section.dataset.brideBundleState = 'loading';
    const country = /^[A-Z]{2}$/.test(config.country || '') ? config.country : null;
    const request = async (query, variables) => {
      const response = await fetch('/api/2026-10/graphql.json', {
        method: 'POST', signal: controller.signal,
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ query, variables: { ...variables, country } })
      });
      const result = await response.json();
      if (!response.ok || result.errors?.length || !result.data) throw new Error('Bundle products could not be loaded.');
      return result.data;
    };
    const lookupQuery = `query BrideBundleComponents($handle: String!, $country: CountryCode) @inContext(country: $country) {
      product(handle: $handle) { variants(first: 1) { nodes { requiresComponents components(first: 3) {
        nodes { quantity productVariant { product { id title handle productType } } }
      } } } }
    }`;
    const productQuery = `query BrideBundlePiece($handle: String!, $after: String, $country: CountryCode) @inContext(country: $country) {
      product(handle: $handle) { id title handle onlineStoreUrl availableForSale featuredImage { url(transform: {maxWidth: 132}) }
        variants(first: 100, after: $after) { nodes { id title availableForSale currentlyNotInStock
          price { amount currencyCode } selectedOptions { name value }
        } pageInfo { hasNextPage endCursor } }
      }
    }`;
    const loadPiece = async component => {
      let after = null;
      let product;
      const variants = [];
      do {
        const data = await request(productQuery, { handle: component.handle, after });
        if (!data.product?.onlineStoreUrl) return null;
        product = data.product;
        if (product.id !== component.id) throw new Error('The bundle component has changed.');
        for (const variant of product.variants.nodes) {
          const id = variant.id.match(/^gid:\/\/shopify\/ProductVariant\/(\d+)$/)?.[1];
          const price = Number(variant.price.amount);
          if (!id || !Number.isFinite(price) || variant.price.currencyCode !== currency) throw new Error('The component price is unavailable in this currency.');
          variants.push({ id, price: Math.round(price * 100), title: variant.title,
            options: variant.selectedOptions.map(option => option.value), available: variant.availableForSale,
            preorder: variant.availableForSale && variant.currentlyNotInStock });
        }
        const page = product.variants.pageInfo;
        if (page.hasNextPage && (!page.endCursor || page.endCursor === after)) throw new Error('Bundle variant pagination failed.');
        after = page.hasNextPage ? page.endCursor : null;
      } while (after);
      const metadata = await loadPreorderData(component.handle, controller.signal);
      if (metadata && String(metadata.product_id) === product.id.split('/').pop()) {
        variants.forEach(variant => {
          variant.preorder_dispatch_week = metadata.preorder_dispatch_week;
          variant.preorder_note = metadata.preorder_note;
        });
      }
      return { ...product, variants };
    };
    const role = component => {
      const name = `${component.productType || ''} ${component.title} ${component.handle}`;
      if (/\bblazer\b|\bjacket\b/i.test(name)) return 'blazer';
      if (/\btrousers?\b|\bpants?\b/i.test(name)) return 'trouser';
      return null;
    };
    const hydrate = pieces => {
      const selectedMode = form.querySelector('[name="bride_purchase"]:checked')?.value || 'suit';
      const previousSizes = new Map([...form.querySelectorAll('[data-bride-piece-options] input:checked')].map(input => [input.name, input.value]));
      const previousInputs = new Map([...form.querySelectorAll('[data-bride-piece-options] input')].map(input => [input.value, input]));
      const groups = [];
      const purchase = document.createElement('fieldset');
      purchase.className = 'bride-product__purchase';
      const legend = document.createElement('legend');
      legend.textContent = config.heading || 'What would you like?';
      const choices = document.createElement('div');
      choices.className = 'bride-product__purchase-grid';
      const addChoice = (mode, label, product = null) => {
        const wrapper = document.createElement('label');
        wrapper.className = 'bride-product__purchase-choice';
        const input = document.createElement('input');
        input.type = 'radio'; input.name = 'bride_purchase'; input.value = mode; input.dataset.label = label;
        if (product) {
          input.dataset.title = product.title;
          input.dataset.price = String(Math.min(...product.variants.map(variant => variant.price)));
          if (product.featuredImage?.url) input.dataset.image = product.featuredImage.url;
          input.disabled = !product.variants.some(variant => variant.available);
        }
        const span = document.createElement('span'); span.textContent = label;
        if (input.disabled) { const sold = document.createElement('small'); sold.textContent = 'Sold out'; span.append(sold); }
        wrapper.append(input, span); choices.append(wrapper);
      };
      addChoice('suit', config.suitLabel || 'Full suit');
      for (const [mode, product] of pieces) {
        if (!product || !product.variants.length || config[mode]?.enabled === false) continue;
        addChoice(mode, config[mode]?.label || (mode === 'blazer' ? 'Blazer only' : 'Trousers only'), product);
        const group = sizeTemplate.content.firstElementChild.cloneNode(true);
        group.dataset.bridePieceOptions = mode;
        const label = group.querySelector('[data-bride-size-label]');
        label.textContent = label.dataset.label = config[mode]?.sizeLabel || (mode === 'blazer' ? 'Blazer size' : 'Trouser size');
        const grid = group.querySelector('.bride-product__size-grid');
        for (const variant of product.variants) {
          const wrapper = document.createElement('label'); wrapper.className = 'bride-size';
          const input = document.createElement('input');
          input.type = 'radio'; input.name = `bride_${mode}_only`; input.value = variant.id;
          input.dataset.price = String(variant.price); input.dataset.preorder = String(variant.preorder);
          input.dataset.preorderDispatchWeek = variant.preorder_dispatch_week || '';
          input.dataset.preorderNote = variant.preorder_note || '';
          input.disabled = !variant.available;
          const previous = previousInputs.get(variant.id);
          if (!input.dataset.preorderNote && previous?.dataset.preorderNote) input.dataset.preorderNote = previous.dataset.preorderNote;
          if (!input.dataset.preorderDispatchWeek && previous?.dataset.preorderDispatchWeek) input.dataset.preorderDispatchWeek = previous.dataset.preorderDispatchWeek;
          if (!variant.preorder && variant.available && previous?.dataset.inventory !== undefined) input.dataset.inventory = previous.dataset.inventory;
          input.checked = !input.disabled && previousSizes.get(input.name) === input.value;
          const span = document.createElement('span');
          span.textContent = variant.options.length === 1 && /^\d+$/.test(variant.title) && Number(variant.title) > 0 ? String(Number(variant.title)) : variant.title;
          wrapper.append(input, span); grid.append(wrapper);
        }
        groups.push(group);
      }
      purchase.append(legend, choices);
      const nextChoice = [...choices.querySelectorAll('input')].find(input => input.value === selectedMode && !input.disabled)
        || choices.querySelector('input');
      nextChoice.checked = true;
      const oldPurchase = form.querySelector('.bride-product__purchase');
      if (oldPurchase) oldPurchase.replaceWith(purchase);
      else form.querySelector('[data-bride-suit-options]').before(purchase);
      form.querySelectorAll('[data-bride-piece-options]').forEach(group => group.remove());
      form.querySelector('[data-bride-suit-options]').after(...groups);
      section.dataset.brideBundleState = 'ready';
      section.brideBundlePieces = new Map(pieces);
      updateSelection();
      section.dispatchEvent(new CustomEvent('bride:product-options-updated'));
    };
    (async () => {
      try {
        const data = await request(lookupQuery, { handle: config.handle });
        const variant = data.product?.variants.nodes[0];
        if (!variant?.requiresComponents) { section.dataset.brideBundleState = 'not-bundle'; return; }
        const components = variant.components.nodes;
        // Only the existing two-piece suit flow is supported; never reinterpret other bundle shapes.
        if (components.length !== 2 || components.some(component => component.quantity !== 1) || section.dataset.pair === 'true') throw new Error('This bundle needs its existing product configuration.');
        const products = components.map(component => component.productVariant.product);
        const roles = products.map(role);
        if (!roles.includes('blazer') || !roles.includes('trouser')) throw new Error('The suit components could not be identified.');
        const pieces = await Promise.all(products.map(loadPiece));
        if (!section.isConnected || controller.signal.aborted) return;
        hydrate(['blazer', 'trouser'].map(mode => [mode, pieces[roles.indexOf(mode)]]));
      } catch (_) {
        if (section.isConnected) section.dataset.brideBundleState = 'fallback';
      } finally { clearTimeout(timeout); }
    })();
    return () => { clearTimeout(timeout); controller.abort(); };
  }

  function initStickyPurchase(section, form, sticky, button, updateSelection) {
    const drawer = sticky.querySelector('[data-bride-sticky-drawer]');
    const host = drawer?.querySelector('[data-bride-sticky-options]');
    const closeButton = drawer?.querySelector('[data-bride-sticky-close]');
    const backdrop = section.querySelector('[data-bride-sticky-backdrop]');
    if (!drawer || !host || !closeButton || !backdrop) return null;
    const brideChoices = section.querySelector('.bride-product__suits');
    const guide = section.querySelector('[data-bride-size-guide]');
    const footer = sticky.querySelector('.bride-sticky__inner');
    let open = false;
    let restoreTimer;
    const moved = [];
    const background = [];
    const focusable = () => [...sticky.querySelectorAll('button:not(:disabled), input:not(:disabled), a[href], [tabindex="0"]')]
      .filter(element => element.tabIndex >= 0 && element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden');
    const restoreControls = () => {
      clearTimeout(restoreTimer);
      moved.splice(0).forEach(({ element, placeholder }) => placeholder.replaceWith(element));
      sticky.classList.remove('is-closing');
    };
    const restoreBackground = () => background.splice(0).forEach(({ element, inert }) => { element.inert = inert; });
    const resizeFooter = () => sticky.style.setProperty('--bride-sticky-footer-height', `${footer.offsetHeight}px`);
    const footerObserver = new ResizeObserver(resizeFooter);
    footerObserver.observe(footer);
    function close(returnFocus = true, immediate = false) {
      if (!open) {
        if (immediate) restoreControls();
        return;
      }
      open = false;
      sticky.classList.remove('is-expanded');
      sticky.classList.add('is-closing');
      sticky.removeAttribute('role');
      sticky.removeAttribute('aria-modal');
      sticky.removeAttribute('aria-labelledby');
      sticky.inert = false;
      drawer.inert = true;
      drawer.setAttribute('aria-hidden', 'true');
      button.setAttribute('aria-expanded', 'false');
      backdrop.classList.remove('is-visible');
      document.body.classList.remove('bride-purchase-open');
      restoreBackground();
      // Keep the controls in place for the slide-down transition, then return them.
      if (immediate || window.matchMedia('(prefers-reduced-motion: reduce)').matches) restoreControls();
      else restoreTimer = setTimeout(restoreControls, 360);
      if (returnFocus) button.focus({ preventScroll: true });
    }
    function show() {
      if (open) return;
      clearTimeout(restoreTimer);
      if (!moved.length) {
        [brideChoices, form].filter(Boolean).forEach(element => {
          const placeholder = document.createElement('div');
          placeholder.style.height = `${element.getBoundingClientRect().height}px`;
          placeholder.setAttribute('aria-hidden', 'true');
          element.before(placeholder);
          moved.push({ element, placeholder });
          host.append(element);
        });
      }
      // Inert the surrounding page without blocking the nested size guide.
      for (let branch = sticky; branch.parentElement && branch !== document.body; branch = branch.parentElement) {
        [...branch.parentElement.children].forEach(element => {
          if (element === branch || element === backdrop || element === guide || ['SCRIPT', 'STYLE', 'LINK'].includes(element.tagName)) return;
          background.push({ element, inert: element.inert });
          element.inert = true;
        });
      }
      open = true;
      sticky.classList.remove('is-closing');
      sticky.classList.add('is-expanded', 'is-visible');
      sticky.inert = false;
      sticky.setAttribute('aria-hidden', 'false');
      sticky.setAttribute('role', 'dialog');
      sticky.setAttribute('aria-modal', 'true');
      sticky.setAttribute('aria-labelledby', drawer.querySelector('h3').id);
      drawer.inert = false;
      drawer.setAttribute('aria-hidden', 'false');
      button.setAttribute('aria-expanded', 'true');
      backdrop.classList.add('is-visible');
      document.body.classList.add('bride-purchase-open');
      host.scrollTop = 0;
      updateSelection();
      resizeFooter();
      closeButton.focus({ preventScroll: true });
    }
    const onKeydown = event => {
      if (!open || guide?.classList.contains('active')) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
      } else if (event.key === 'Tab') {
        const controls = focusable();
        const first = controls[0];
        const last = controls[controls.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault(); last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault(); first?.focus();
        }
      }
    };
    const guideObserver = new MutationObserver(() => {
      if (open) {
        sticky.inert = guide.classList.contains('active');
        // The shared modal clears theme inert regions on hide; retain this drawer's lock.
        background.forEach(({ element }) => { element.inert = true; });
      }
    });
    if (guide) guideObserver.observe(guide, { attributes: true, attributeFilter: ['class'] });
    closeButton.addEventListener('click', () => close());
    backdrop.addEventListener('click', () => close());
    sticky.addEventListener('keydown', onKeydown);
    return {
      isOpen: () => open, open: show, close,
      destroy() {
        close(false, true);
        restoreControls();
        footerObserver.disconnect();
        guideObserver.disconnect();
        sticky.removeEventListener('keydown', onKeydown);
      }
    };
  }

  function initSizeGuide(section, form, matchSuitVariant = null, activeGroups = null) {
    const modal = section.querySelector('[data-bride-size-guide]');
    const triggers = () => [...section.querySelectorAll('[data-bride-size-open]')];
    if (!modal || !triggers().length || modal.dataset.brideReady) return;
    let activeTrigger = triggers()[0];
    modal.dataset.brideReady = 'true';
    const tabs = [...modal.querySelectorAll('[data-bride-size-tab]')];
    const panels = [...modal.querySelectorAll('[data-bride-size-panel]')];
    const suggestion = modal.querySelector('[data-bride-fit-suggestion]');
    const note = modal.querySelector('[data-bride-fit-note]');
    const questions = modal.querySelector('[data-bride-fit-questions]');
    if (!questions) return;
    const defaultNote = note.textContent;
    const apply = modal.querySelector('[data-bride-fit-apply]');
    const availability = modal.querySelector('[data-bride-fit-availability]');
    const contact = modal.querySelector('[data-bride-fit-contact]');
    const heightGroup = modal.querySelector('[data-bride-fit-height-group]');
    const native = Boolean(matchSuitVariant);
    const pair = section.dataset.pair === 'true' || native;
    const groups = activeGroups || (() => pair ? ['bride_blazer', 'bride_trouser'] : ['bride_variant']);
    let closingUntil = 0;
    let opening = false;
    const remembered = new Map();

    const sizeOf = input => {
      const text = input.nextElementSibling?.textContent.trim() || '';
      const match = text.match(/^(?:UK\s*|Size\s*)?(\d{1,2})$/i);
      if (match && Number(match[1]) > 0) return String(Number(match[1]));
      return /^(?:XXS|XS|S|M|L|XL|XXL|[2-6]XL)$/i.test(text) ? text.toUpperCase() : null;
    };
    const roleOf = name => name.includes('blazer') ? 'blazer' : name.includes('trouser') ? 'trouser' : 'size';
    const labelOf = name => roleOf(name) === 'blazer' ? 'Blazer' : roleOf(name) === 'trouser' ? 'Trousers' : 'Size';
    const choices = () => [...questions.querySelectorAll('[data-bride-fit-group]')];
    function rememberSizes() {
      choices().forEach(group => {
        const selected = group.querySelector('input:checked');
        if (selected) remembered.set(roleOf(group.dataset.brideFitGroup), selected.value);
      });
    }
    function renderQuestions(prefill = true) {
      rememberSizes();
      questions.replaceChildren();
      groups().forEach(name => {
        const inputs = [...form.querySelectorAll(`input[name="${name}"]`)];
        const values = [...new Set(inputs.map(sizeOf).filter(Boolean))];
        if (!values.length) return;
        const numeric = values.every(value => /^\d+$/.test(value));
        if (numeric) values.sort((a, b) => Number(a) - Number(b));
        const fieldset = document.createElement('fieldset');
        fieldset.dataset.brideFitGroup = name;
        const legend = document.createElement('legend');
        legend.className = 'text-size--regular';
        const garment = roleOf(name) === 'blazer' ? 'blazer ' : roleOf(name) === 'trouser' ? 'trouser ' : '';
        legend.textContent = `Your usual ${numeric ? 'UK ' : ''}${garment}size`;
        const grid = document.createElement('div');
        grid.className = 'bride-size-guide__sizes';
        const selected = prefill ? inputs.find(input => input.checked && sizeOf(input)) : null;
        const chosen = (selected && sizeOf(selected)) || remembered.get(roleOf(name));
        values.forEach(value => {
          const wrapper = document.createElement('label'); wrapper.className = 'bride-size text-size--smaller';
          const input = document.createElement('input');
          input.type = 'radio'; input.name = `${modal.id}-fit-${roleOf(name)}`; input.value = value;
          input.dataset.brideFitSize = ''; input.checked = value === chosen;
          const span = document.createElement('span'); span.textContent = value;
          wrapper.append(input, span); grid.append(wrapper);
        });
        fieldset.append(legend, grid); questions.append(fieldset);
      });
      if (heightGroup) heightGroup.hidden = groups().length === 1 && roleOf(groups()[0]) === 'blazer';
    }
    function updateSuggestion() {
      rememberSizes();
      const names = groups();
      const height = !heightGroup?.hidden ? modal.querySelector('[data-bride-fit-height]:checked')?.value : null;
      note.textContent = height ? note.dataset[`${height}Note`] || defaultNote : defaultNote;
      if (contact) contact.hidden = true;
      apply.hidden = false;
      apply.textContent = names.length === 1 ? 'Use this size' : 'Use these sizes';
      const sizes = names.map(name => choices().find(group => group.dataset.brideFitGroup === name)?.querySelector('input:checked')?.value);
      const summary = names.map((name, index) => sizes[index] ? `${labelOf(name)} ${sizes[index]}` : `Choose ${roleOf(name) === 'size' ? 'your' : roleOf(name)} size`).join(' · ');
      suggestion.textContent = summary;
      if (choices().length !== names.length) {
        availability.textContent = 'This product needs additional options. Choose them on the product or check its size chart.';
        apply.disabled = true;
        return null;
      }
      if (sizes.some(size => !size)) {
        availability.textContent = names.length === 1 ? 'Choose your usual size to check availability.' : 'Your blazer and trousers can be different sizes. Choose both to check availability.';
        apply.disabled = true;
        return null;
      }
      const nativeSuit = native && names.includes('bride_blazer') && names.includes('bride_trouser');
      const selections = names.map((name, index) => [...form.querySelectorAll(`input[name="${name}"]`)]
        .find(input => sizeOf(input) === sizes[index]));
      const variant = nativeSuit && selections.every(Boolean) ? matchSuitVariant(selections[0].value, selections[1].value) : null;
      const available = selections.every(Boolean)
        && (nativeSuit ? variant?.available : selections.every(input => !input.disabled));
      const preorders = (nativeSuit ? section.brideSuitPreorders?.(selections[0], selections[1], variant) || [variant] : selections).filter(isPreorder);
      apply.disabled = !available;
      if (available) {
        const preorder = preorders.length > 0;
        availability.textContent = preorder
          ? preorderMessages(preorders, section.dataset.preorderNote || defaultPreorderNote)
          : names.length === 1 ? 'This size is available.' : 'This size combination is available.';
        if (preorder) apply.textContent = names.length === 1 ? 'Use pre-order size' : 'Use pre-order sizes';
      } else {
        apply.hidden = true;
        const unavailable = names.filter((_, index) => !selections[index] || (!nativeSuit && selections[index].disabled));
        availability.textContent = nativeSuit ? 'This size combination is currently unavailable. Adjust either size or ask the Skipper for help.'
          : `${unavailable.map(name => `${labelOf(name)} ${sizes[names.indexOf(name)]}`).join(' and ')} ${unavailable.length === 1 ? 'is' : 'are'} currently unavailable. Check the size chart or ask the Skipper for help.`;
        if (contact) {
          const productTitle = form.querySelector('[name="bride_purchase"]:checked')?.dataset.title || section.querySelector('h2')?.textContent.trim() || 'this product';
          const heightLabel = height ? modal.querySelector('[data-bride-fit-height]:checked')?.nextElementSibling.textContent.trim() : '';
          contact.href = `mailto:${contact.dataset.email}?subject=${encodeURIComponent(`Size advice — ${productTitle}`)}&body=${encodeURIComponent(`Hello Skipper,\n\nPlease help me choose sizes for ${productTitle}.\nMy usual sizes: ${summary}${heightLabel ? `\nHeight: ${heightLabel}` : ''}\n\n${window.location.href.split('#')[0]}`)}`;
          contact.setAttribute('aria-label', `Ask the Skipper about ${summary}`);
          contact.hidden = false;
        }
      }
      return available ? selections : null;
    }
    function selectTab(tab, focus = false) {
      tabs.forEach(item => {
        const selected = item === tab;
        item.setAttribute('aria-selected', String(selected));
        item.tabIndex = selected ? 0 : -1;
      });
      panels.forEach(panel => { panel.hidden = panel.dataset.brideSizePanel !== tab.dataset.brideSizeTab; });
      if (focus) tab.focus();
    }
    function close() {
      // The shared modal finishes hiding after 500ms. Let that finish before reopening.
      closingUntil = performance.now() + 520;
      modal.hide(false);
      // Restore the purchase drawer before returning focus to its size-guide trigger.
      const purchase = form.closest('[data-bride-sticky]');
      if (purchase) purchase.inert = false;
      triggers().forEach(trigger => trigger.setAttribute('aria-expanded', 'false'));
      activeTrigger.focus({ preventScroll: true });
    }
    section.addEventListener('click', async event => {
      const trigger = event.target.closest('[data-bride-size-open]');
      if (!trigger || opening) return;
      opening = true;
      activeTrigger = trigger;
      try {
        await customElements.whenDefined('modal-box');
        const delay = closingUntil - performance.now();
        if (delay > 0) await new Promise(resolve => setTimeout(resolve, delay));
        if (!modal.isConnected) return;
        renderQuestions();
        updateSuggestion();
        modal.show();
        trigger.setAttribute('aria-expanded', 'true');
      } finally { opening = false; }
    });
    modal.querySelectorAll('[data-bride-size-close]').forEach(button => button.addEventListener('click', close));
    tabs.forEach(tab => tab.addEventListener('click', () => selectTab(tab)));
    modal.querySelector('[data-bride-fit-chart]')?.addEventListener('click', () => {
      const chart = tabs.find(tab => tab.dataset.brideSizeTab === 'chart');
      if (chart) selectTab(chart, true);
    });
    modal.addEventListener('change', updateSuggestion);
    const refreshQuestions = () => {
      if (!modal.classList.contains('active')) return;
      renderQuestions(false);
      updateSuggestion();
    };
    form.addEventListener('change', refreshQuestions);
    section.addEventListener('bride:product-options-updated', refreshQuestions);
    apply.addEventListener('click', () => {
      const selections = updateSuggestion();
      if (!selections) return;
      selections.forEach(input => { input.checked = true; });
      selections[selections.length - 1].dispatchEvent(new Event('change', { bubbles: true }));
      close();
      if (!form.closest('[data-bride-sticky]')) selections[0].closest('fieldset')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
    modal.addEventListener('keydown', event => {
      if (!modal.classList.contains('active')) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
      } else if (event.target.matches('[data-bride-size-tab]') && ['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
        event.preventDefault();
        const index = tabs.indexOf(event.target);
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1
          : (index + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
        selectTab(tabs[next], true);
      } else if (event.key === 'Tab') {
        const focusable = [...modal.querySelectorAll('button:not(:disabled), input:not(:disabled), a[href], [tabindex="0"]')]
          .filter(element => element.tabIndex >= 0 && element.getClientRects().length > 0);
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && (document.activeElement === first || document.activeElement === modal)) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first?.focus();
        }
      }
    });
  }

  function initSuitQuickAdd(section) {
    if (section.dataset.brideQuickReady) return;
    section.dataset.brideQuickReady = 'true';
    const modal = section.querySelector('[data-bride-suit-modal]');
    if (!modal) return;
    const form = modal.querySelector('[data-bride-card-form]');
    const options = modal.querySelector('[data-bride-card-options]');
    const add = modal.querySelector('[data-bride-card-add]');
    const error = modal.querySelector('[data-bride-card-error]');
    const preorderNotice = modal.querySelector('[data-bride-card-preorder-note]');
    const sectionError = section.querySelector('[data-bride-suits-error]');
    const buttons = () => [...section.querySelectorAll('[data-bride-suit-quick]')];
    let currentVariants = [];
    let activeTrigger;
    let busy = false;
    let opening = false;
    let closingUntil = 0;
    const dispatchSources = new Map();
    const dispatchPending = new Set();
    const groups = () => [...options.querySelectorAll('[data-bride-card-option-group]')];
    const values = () => groups().map(group => group.querySelector('input:checked')?.value);
    const selected = () => {
      const choices = values();
      if (!choices.length || choices.some(value => value === undefined)) return null;
      return currentVariants.find(variant => variant.available && choices.every((value, index) => variant.options[index] === value));
    };
    function updateOptions() {
      const rows = groups();
      rows.forEach((group, index) => {
        const preceding = rows.slice(0, index).map(row => row.querySelector('input:checked')?.value);
        group.querySelectorAll('input').forEach(input => {
          const available = currentVariants.some(variant => variant.available && variant.options[index] === input.value
            && preceding.every((value, position) => value === undefined || variant.options[position] === value));
          input.disabled = busy || !available;
          if (!available) input.checked = false;
        });
      });
      const choices = values();
      const match = selected();
      const candidate = match || currentVariants.find(variant => variant.available
        && choices.every((value, index) => value === undefined || variant.options[index] === value));
      modal.querySelector('[data-bride-card-price]').textContent = candidate ? money(candidate.price) : '';
      if (isPreorder(match) && !dispatchSources.has(match.id) && !dispatchPending.has(match.id)) {
        dispatchPending.add(match.id);
        quickPreorderSources(match, activeTrigger).then(sources => {
          dispatchSources.set(match.id, sources);
          dispatchPending.delete(match.id);
          if (selected()?.id === match.id) updateOptions();
        });
      }
      const checkingDates = Boolean(match && dispatchPending.has(match.id));
      add.disabled = busy || !match || checkingDates;
      add.textContent = busy ? 'Adding…' : checkingDates ? 'Checking dates…' : isPreorder(match) ? 'Pre-order' : 'Add to Cart';
      if (preorderNotice) {
        preorderNotice.textContent = checkingDates ? 'Checking this preorder’s dispatch details…' : preorderMessages(dispatchSources.get(match?.id) || [], activeTrigger?.dataset.preorderNote || defaultPreorderNote);
        preorderNotice.hidden = !isPreorder(match);
      }
    }
    function close(returnFocus = true) {
      if (!modal.classList.contains('active')) return;
      closingUntil = performance.now() + 520;
      modal.inert = true;
      modal.hide(false);
      activeTrigger?.setAttribute('aria-expanded', 'false');
      if (returnFocus) activeTrigger?.focus({ preventScroll: true });
    }
    function setBusy(value, trigger = activeTrigger) {
      busy = value;
      buttons().forEach(button => {
        if (!button.hasAttribute('data-sold-out')) button.dataset.soldOut = String(button.disabled);
        button.disabled = value || button.dataset.soldOut === 'true';
      });
      updateOptions();
      if (trigger) {
        trigger.textContent = value ? 'Adding…' : (trigger.dataset.quickLabel || (trigger.dataset.soldOut === 'true' ? 'Sold out' : 'Quick add'));
        if (value) trigger.setAttribute('aria-busy', 'true');
        else trigger.removeAttribute('aria-busy');
      }
    }
    buttons().forEach(button => { button.dataset.soldOut = String(button.disabled); });
    async function submitVariant(variant, trigger, fromModal) {
      if (busy || !variant?.available) return;
      setBusy(true, trigger);
      showError(fromModal ? error : sectionError, '');
      try {
        const sources = isPreorder(variant) ? dispatchSources.get(variant.id) || await quickPreorderSources(variant, trigger) : [];
        await addItems([{ id: Number(variant.id), quantity: 1, ...(isPreorder(variant) ? { properties: bundlePreorderProperties(sources) } : {}) }]);
        close(false);
        document.dispatchEvent(new CustomEvent('bride:cart-added'));
      } catch (failure) {
        showError(fromModal ? error : sectionError, failure.message);
        if (!fromModal) sectionError?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      } finally {
        setBusy(false, trigger);
        if (fromModal && modal.classList.contains('active')) add.focus({ preventScroll: true });
      }
    }
    const onClick = async event => {
      const trigger = event.target.closest('[data-bride-suit-quick]');
      if (!trigger || busy || opening || trigger.disabled) return;
      event.preventDefault();
      const card = trigger.closest('.bride-suits__card, [data-js-product-item]');
      let variants;
      let optionNames;
      try {
        variants = JSON.parse(card.querySelector('[data-bride-card-variants]').textContent);
        optionNames = JSON.parse(card.querySelector('[data-bride-card-option-names]')?.textContent || '[]');
      }
      catch (_) { showError(sectionError, 'Could not load this product’s options. Open its product page to choose a size.'); return; }
      showError(sectionError, '');
      if (variants.length === 1) {
        await submitVariant(variants[0], trigger, false);
        return;
      }
      opening = true;
      try {
        await customElements.whenDefined('modal-box');
        const delay = closingUntil - performance.now();
        if (delay > 0) await new Promise(resolve => setTimeout(resolve, delay));
        if (!section.isConnected || !trigger.isConnected) return;
        currentVariants = variants.map(variant => ({ ...variant, options: variant.options || [variant.title] }));
        activeTrigger = trigger;
        modal.querySelector('[data-bride-card-title]').textContent = trigger.dataset.title;
        options.replaceChildren();
        (currentVariants[0]?.options || []).forEach((_, index) => {
          const name = optionNames[index] || `Option ${index + 1}`;
          const row = document.createElement('fieldset');
          row.className = 'bride-product__sizes';
          row.dataset.brideCardOptionGroup = '';
          const legend = document.createElement('legend');
          legend.textContent = name.replace(/\s*\(\s*size\s*\)/gi, ' size');
          const grid = document.createElement('div');
          grid.className = 'bride-suit-quick__variants';
          const sizes = /size|blazer|jacket|trouser|pant/i.test(name);
          if (sizes) grid.classList.add('bride-suit-quick__variants--sizes');
          [...new Set(currentVariants.map(variant => variant.options[index]))].forEach(value => {
            const label = document.createElement('label');
            label.className = 'bride-size';
            const input = document.createElement('input');
            input.type = 'radio';
            input.name = `${modal.id}-option-${index}`;
            input.value = value;
            input.dataset.brideCardOption = '';
            input.dataset.optionIndex = String(index);
            const text = document.createElement('span');
            text.textContent = sizes && /^0[1-9]$/.test(value) ? String(Number(value)) : value;
            label.append(input, text);
            grid.append(label);
          });
          row.append(legend, grid);
          options.append(row);
        });
        updateOptions();
        showError(error, currentVariants.some(variant => variant.available) ? '' : 'This product is currently sold out.');
        modal.inert = false;
        modal.show();
        trigger.setAttribute('aria-expanded', 'true');
      } finally { opening = false; }
    };
    section.addEventListener('click', onClick);
    form.addEventListener('change', () => {
      updateOptions();
      showError(error, '');
    });
    form.addEventListener('submit', event => {
      event.preventDefault();
      if (!selected()) { showError(error, 'Choose an available value in each option row.'); return; }
      submitVariant(selected(), activeTrigger, true);
    });
    modal.querySelectorAll('[data-bride-card-close]').forEach(button => button.addEventListener('click', () => close()));
    modal.addEventListener('keydown', event => {
      if (!modal.classList.contains('active')) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
      } else if (event.key === 'Tab') {
        const controls = [...modal.querySelectorAll('button:not(:disabled), input:not(:disabled), a[href]')]
          .filter(element => element.tabIndex >= 0 && element.getClientRects().length);
        const first = controls[0];
        const last = controls[controls.length - 1];
        if (event.shiftKey && (document.activeElement === first || document.activeElement === modal)) {
          event.preventDefault(); last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault(); first?.focus();
        }
      }
    });
    // Facets replaces the grid in place. Delegated events read the new cards;
    // close an open dialog if its source card was replaced.
    const grid = section.querySelector('#CollectionProductGrid');
    const observer = grid ? new MutationObserver(() => {
      if (activeTrigger && !activeTrigger.isConnected) close(false);
    }) : null;
    if (grid) observer.observe(grid, { childList: true, subtree: true });
    const beforeCartOpen = () => { if (busy) close(false); };
    document.addEventListener('bride:cart-updated', beforeCartOpen);
    section.closest('.shopify-section')?.addEventListener('shopify:section:unload', () => {
      close(false);
      observer?.disconnect();
      section.removeEventListener('click', onClick);
      document.removeEventListener('bride:cart-updated', beforeCartOpen);
    }, { once: true });
  }

  function initLook(section) {
    if (section.dataset.brideReady) return;
    section.dataset.brideReady = 'true';
    initLookControls(section);
    const error = section.querySelector('[data-bride-look-error]');
    const count = section.querySelector('[data-bride-look-count]');
    const wholeButton = section.querySelector('[data-bride-add-look]');
    const cards = [...section.querySelectorAll('[data-bride-look-item]')];
    let cartIds = new Set();
    let cartLines = [];
    let busy = false;
    function renderCartState() {
      const remaining = cards.filter(card => !cartIds.has(Number(card.dataset.variantId)));
      cards.forEach(card => {
        const quick = card.querySelector('[data-bride-quick-add]');
        if (!quick) return;
        const inCart = cartIds.has(Number(card.dataset.variantId));
        quick.textContent = inCart ? 'In cart ✓' : isPreorder(card) ? 'Pre-order' : 'Quick add';
        quick.disabled = busy;
        quick.setAttribute('aria-pressed', String(inCart));
        quick.setAttribute('aria-label', inCart ? `Remove ${card.querySelector('h3')?.textContent?.trim() || 'piece'} from your cart` : `${isPreorder(card) ? 'Pre-order' : 'Add'} ${card.querySelector('h3')?.textContent?.trim() || 'piece'}${isPreorder(card) ? '' : ' to your cart'}`);
        quick.classList.toggle('is-in-catch', inCart);
      });
      document.querySelectorAll('[data-bride-cart-recommendation]').forEach(button => {
        const inCart = cartIds.has(Number(button.dataset.brideCartRecommendation));
        const preorder = cards.some(card => card.dataset.variantId === button.dataset.brideCartRecommendation && isPreorder(card));
        button.textContent = inCart ? 'Added ✓' : preorder ? 'Pre-order' : '+ Add';
        button.setAttribute('aria-label', inCart ? 'Remove this piece from your cart' : preorder ? 'Pre-order this piece' : 'Add this piece to your cart');
        button.disabled = busy;
      });
      const fullPrice = cards.reduce((sum, card) => sum + Number(card.dataset.price || 0), 0);
      const remainingPrice = remaining.reduce((sum, card) => sum + Number(card.dataset.price || 0), 0);
      if (count) count.textContent = remaining.length
        ? `${cards.length - remaining.length} of ${cards.length} in your cart${remaining.length < cards.length ? ` · ${money(fullPrice - remainingPrice)} of ${money(fullPrice)} chosen` : ''}`
        : `All set for the big day! · ${money(fullPrice)} full look`;
      if (wholeButton) {
        const preorder = remaining.some(isPreorder);
        const action = preorder ? 'Pre-order' : 'Add';
        const label = remaining.length === cards.length
          ? (preorder ? 'Pre-order the whole look' : wholeButton.dataset.addLabel || 'Add the whole look')
          : `${action} remaining ${remaining.length} ${remaining.length === 1 ? 'piece' : 'pieces'}`;
        wholeButton.textContent = remaining.length
          ? `${label} — ${money(remainingPrice)}`
          : 'View cart ✓';
        wholeButton.disabled = busy || remaining.some(card => card.dataset.available !== 'true' || card.dataset.wholeEligible !== 'true');
      }
    }
    async function refreshCount() {
      try {
        const response = await fetch(cartUrl(), { headers: { 'Accept': 'application/json' } });
        if (!response.ok) return;
        const cart = await response.json();
        cartLines = cart.items || [];
        cartIds = new Set(cartLines.map(item => Number(item.variant_id)));
        renderCartState();
      } catch (_) { /* cart count is supplemental */ }
    }
    section.addEventListener('click', async event => {
      const quick = event.target.closest('[data-bride-quick-add]');
      const all = event.target.closest('[data-bride-add-look]');
      if (!quick && !all) return;
      const button = quick || all;
      if (busy) return;
      const ids = quick ? [Number(quick.dataset.variantId)] : cards
        .filter(card => !cartIds.has(Number(card.dataset.variantId)))
        .map(card => Number(card.dataset.variantId))
        .filter(id => Number.isFinite(id));
      if (!ids.length) {
        if (all) {
          const drawer = document.querySelector('[data-js-site-cart-sidebar], #site-cart-sidebar');
          if (typeof drawer?.show === 'function') drawer.show();
          else window.location.href = window.KROWN?.settings?.routes?.cart_url || '/cart';
        }
        return;
      }
      busy = true;
      renderCartState();
      showError(error, '');
      try {
        if (quick && cartIds.has(ids[0])) {
          const line = cartLines.find(item => Number(item.variant_id) === ids[0] && item.properties?._BrideLook)
            || cartLines.find(item => Number(item.variant_id) === ids[0]);
          if (!line) throw new Error('This piece is no longer in your cart. Refresh and try again.');
          await changeItem(line.key, Math.max(0, Number(line.quantity) - 1));
        } else {
          await addItems(ids.map(id => ({ id, quantity: 1, properties: { _BrideLook: 'KIPPER Bride complete look', ...preorderProperties(cards.find(card => Number(card.dataset.variantId) === id)) } })));
        }
        await refreshCount();
        document.dispatchEvent(new CustomEvent('bride:cart-added'));
      } catch (failure) {
        showError(error, failure.message);
        await refreshCount();
      } finally { busy = false; renderCartState(); }
    });
    document.addEventListener('bride:cart-added', refreshCount);
    document.getElementById('AjaxCartForm')?.addEventListener('cart-updated', refreshCount);
    refreshCount();
  }

  async function initLookControls(section) {
    const slider = section.querySelector('.bride-complete-look__slider');
    const controls = section.querySelector('.bride-complete-look__controls');
    const track = section.querySelector('[data-bride-look-scrollbar]');
    const thumb = track?.querySelector('[data-bride-look-thumb]');
    if (!slider || !controls || !track || !thumb) return;
    await customElements.whenDefined('css-slider');
    if (!section.isConnected) return;
    const navigation = slider.navigation;
    if (navigation) {
      controls.append(navigation);
      navigation.querySelectorAll('button').forEach(button => { button.type = 'button'; });
    }
    const previous = navigation?.querySelector('.css-slider-navigation__prev');
    const next = navigation?.querySelector('.css-slider-navigation__next');
    for (const [button, glyph] of [[previous, '←'], [next, '→']]) {
      if (!button) continue;
      button.querySelector('svg')?.remove();
      const icon = document.createElement('span');
      icon.className = 'bride-complete-look__arrow-glyph';
      icon.setAttribute('aria-hidden', 'true');
      icon.textContent = glyph;
      button.append(icon);
    }
    const rtl = getComputedStyle(slider).direction === 'rtl';
    const scrollPosition = () => Math.max(0, rtl ? -slider.scrollLeft : slider.scrollLeft);
    const scrollTo = (position, behavior = 'smooth') => slider.scrollTo({ left: rtl ? -position : position, behavior });
    const limit = () => Math.max(0, slider.scrollWidth - slider.clientWidth);
    let frame = 0;
    let dragging = null;

    function update() {
      frame = 0;
      const maximum = limit();
      const overflow = maximum > 1;
      track.hidden = !overflow;
      if (navigation) navigation.hidden = !overflow;
      const position = Math.min(maximum, scrollPosition());
      const progress = maximum ? position / maximum : 0;
      const width = Math.min(track.clientWidth, Math.max(24, track.clientWidth * slider.clientWidth / Math.max(1, slider.scrollWidth)));
      thumb.style.width = `${width}px`;
      thumb.style.transform = `translateX(${(rtl ? -1 : 1) * progress * (track.clientWidth - width)}px)`;
      track.setAttribute('aria-valuenow', String(Math.round(progress * 100)));
      track.setAttribute('aria-valuetext', `${Math.round(progress * 100)}% scrolled`);
      for (const [button, disabled] of [[previous, position <= 1], [next, position >= maximum - 1]]) {
        if (!button) continue;
        button.disabled = disabled;
        button.classList.toggle('disabled', disabled);
        button.setAttribute('aria-disabled', String(disabled));
      }
    }
    const scheduleUpdate = () => { if (!frame) frame = requestAnimationFrame(update); };
    slider.addEventListener('scroll', scheduleUpdate, { passive: true });
    slider.addEventListener('change', scheduleUpdate);
    slider.addEventListener('ready', scheduleUpdate);
    const resize = new ResizeObserver(scheduleUpdate);
    resize.observe(slider);
    resize.observe(track);
    const changes = new MutationObserver(scheduleUpdate);
    changes.observe(slider.querySelector('.css-slides-container'), { childList: true });

    function dragTo(event) {
      const rect = track.getBoundingClientRect();
      const pointer = rtl ? rect.right - event.clientX : event.clientX - rect.left;
      const travel = track.clientWidth - thumb.getBoundingClientRect().width;
      const progress = travel > 0 ? Math.max(0, Math.min(1, (pointer - dragging.offset) / travel)) : 0;
      scrollTo(progress * limit(), 'auto');
      scheduleUpdate();
    }
    track.addEventListener('pointerdown', event => {
      if (event.button !== 0 || track.hidden) return;
      event.preventDefault();
      track.focus({ preventScroll: true });
      const rect = thumb.getBoundingClientRect();
      dragging = { id: event.pointerId, offset: event.target === thumb
        ? (rtl ? rect.right - event.clientX : event.clientX - rect.left) : rect.width / 2 };
      slider.classList.add('disable-snapping', 'bride-look-is-dragging');
      track.setPointerCapture(event.pointerId);
      dragTo(event);
    });
    track.addEventListener('pointermove', event => { if (dragging?.id === event.pointerId) dragTo(event); });
    const finishDrag = event => {
      if (dragging?.id !== event.pointerId) return;
      dragging = null;
      slider.classList.remove('disable-snapping', 'bride-look-is-dragging');
      scheduleUpdate();
    };
    track.addEventListener('pointerup', finishDrag);
    track.addEventListener('pointercancel', finishDrag);
    track.addEventListener('lostpointercapture', finishDrag);
    track.addEventListener('keydown', event => {
      const keys = ['ArrowLeft', 'ArrowRight', 'Home', 'End', 'PageUp', 'PageDown'];
      if (!keys.includes(event.key)) return;
      event.preventDefault();
      const card = slider.querySelector('.bride-look-card');
      const step = card ? card.getBoundingClientRect().width + 16 : slider.clientWidth;
      const direction = event.key === 'ArrowLeft' ? (rtl ? 1 : -1) : event.key === 'ArrowRight' ? (rtl ? -1 : 1)
        : event.key === 'PageUp' ? -1 : 1;
      const distance = event.key.startsWith('Page') ? slider.clientWidth : step;
      const destination = event.key === 'Home' ? 0 : event.key === 'End' ? limit() : scrollPosition() + direction * distance;
      scrollTo(Math.max(0, Math.min(limit(), destination)));
    });
    section.closest('.shopify-section')?.addEventListener('shopify:section:unload', () => {
      resize.disconnect();
      changes.disconnect();
      if (frame) cancelAnimationFrame(frame);
    }, { once: true });
    update();
  }

  function initVideo(dialog) {
    if (dialog.dataset.brideReady) return;
    dialog.dataset.brideReady = 'true';
    const triggers = dialog.closest('.bride-hero')?.querySelectorAll('[data-bride-video-open]');
    const close = dialog.querySelector('[data-bride-video-close]');
    const iframe = dialog.querySelector('[data-bride-instagram-url]');
    let embedUrl;
    if (iframe) {
      try {
        const url = new URL(iframe.dataset.brideInstagramUrl);
        const post = url.pathname.match(/^\/(reel|p|tv)\/([A-Za-z0-9_-]+)(?:\/embed)?\/?$/);
        if (url.protocol === 'https:' && ['instagram.com', 'www.instagram.com'].includes(url.hostname) && post) {
          embedUrl = `https://www.instagram.com/${post[1]}/${post[2]}/embed/`;
        }
      } catch (_) { /* The external link remains available if the setting is invalid. */ }
    }
    let trigger;
    let heldScrollLock = false;
    let closeTimer;
    const release = () => {
      if (dialog.open || !trigger) return;
      clearTimeout(closeTimer);
      closeTimer = undefined;
      dialog.classList.remove('bride-video-dialog--closing');
      // Removing the source unloads the cross-origin player and stops its audio.
      iframe?.removeAttribute('src');
      dialog.querySelector('video')?.pause();
      if (!heldScrollLock) document.body.classList.remove('no-overflow');
      const previousTrigger = trigger;
      trigger = null;
      previousTrigger.focus({ preventScroll: true });
    };
    const dismiss = () => {
      if (!dialog.open || dialog.classList.contains('bride-video-dialog--closing')) return;
      iframe?.removeAttribute('src');
      dialog.querySelector('video')?.pause();
      dialog.classList.add('bride-video-dialog--closing');
      closeTimer = setTimeout(() => {
        if (dialog.open) dialog.close();
        release();
      }, dialog.classList.contains('bride-video-dialog--instagram') && !window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 600 : 0);
    };
    triggers?.forEach(button => button.addEventListener('click', event => {
      event.preventDefault();
      if (dialog.open) {
        if (!dialog.classList.contains('bride-video-dialog--closing')) return;
        clearTimeout(closeTimer);
        closeTimer = undefined;
        dialog.classList.remove('bride-video-dialog--closing');
        trigger = button;
        if (embedUrl) iframe.src = embedUrl;
        return;
      }
      trigger = button;
      heldScrollLock = document.body.classList.contains('no-overflow');
      document.body.classList.add('no-overflow');
      if (embedUrl) iframe.src = embedUrl;
      dialog.showModal();
    }));
    close?.addEventListener('click', dismiss);
    dialog.addEventListener('close', release);
    dialog.addEventListener('cancel', event => {
      event.preventDefault();
      dismiss();
    });
    dialog.addEventListener('click', event => { if (event.target === dialog) dismiss(); });
    dialog.closest('.shopify-section')?.addEventListener('shopify:section:unload', () => {
      if (dialog.open) {
        clearTimeout(closeTimer);
        dialog.close();
        release();
      }
    }, { once: true });
  }

  function initReviews(section) {
    if (section.dataset.brideReady) return;
    section.dataset.brideReady = 'true';
    const fitMarker = section.querySelector('[data-bride-fit-marker]');
    if (fitMarker) {
      const position = Number(section.dataset.brideFitPosition);
      fitMarker.style.left = `${Number.isFinite(position) ? Math.max(0, Math.min(100, position)) : 52}%`;
    }
    const updateSummary = () => {
      const raw = window.jdgm?.data?.reviewWidget?.[section.dataset.brideReviewProductId];
      let data = raw;
      if (typeof raw === 'string') {
        try { data = JSON.parse(raw); } catch { data = null; }
      }
      const count = Number(data?.number_of_reviews ?? section.dataset.brideReviewCount ?? 0);
      const average = Number(data?.average_rating ?? section.dataset.brideReviewAverage ?? 0);
      const safeCount = Number.isFinite(count) ? Math.max(0, count) : 0;
      const safeAverage = Number.isFinite(average) ? Math.max(0, Math.min(5, average)) : 0;
      const averageText = section.querySelector('[data-bride-review-average-text]');
      const countText = section.querySelector('[data-bride-review-count-text]');
      if (averageText) averageText.textContent = safeCount ? safeAverage.toFixed(1) : '—';
      if (countText) countText.textContent = safeCount ? `from ${safeCount} ${safeCount === 1 ? 'review' : 'reviews'}` : 'No bride reviews yet';
      const empty = section.querySelector('.bride-reviews__empty');
      if (empty) empty.hidden = safeCount > 0;
      section.querySelector('.bride-reviews__stars')?.style.setProperty('--bride-review-star-fill', `${safeCount ? safeAverage / 5 * 100 : 0}%`);
      const histogram = Array.isArray(data?.histogram) ? data.histogram : null;
      const note = section.querySelector('[data-bride-review-histogram-note]');
      section.querySelectorAll('[data-bride-review-rating]').forEach(row => {
        const item = histogram?.find(entry => Number(entry.rating) === Number(row.dataset.brideReviewRating));
        row.hidden = safeCount > 0 && !histogram;
        const frequency = Number(item?.frequency ?? 0);
        const percentage = Number(item?.percentage ?? 0);
        row.querySelector('[data-bride-review-frequency]').textContent = String(Number.isFinite(frequency) ? frequency : 0);
        row.querySelector('[data-bride-review-bar]').style.width = `${Number.isFinite(percentage) ? Math.max(0, Math.min(100, percentage)) : 0}%`;
      });
      if (note) note.hidden = !safeCount || !!histogram;
    };
    updateSummary();
    const widget = section.querySelector('#judgeme_product_reviews');
    if (widget) {
      const observer = new MutationObserver(updateSummary);
      observer.observe(widget, { childList: true, subtree: true });
      section.closest('.shopify-section')?.addEventListener('shopify:section:unload', () => observer.disconnect(), { once: true });
    }
    section.querySelector('[data-bride-review-write]')?.addEventListener('click', event => {
      const writeButton = section.querySelector('#judgeme_product_reviews [data-testid="write-review-button"], #judgeme_product_reviews .jdgm-write-rev-link');
      if (!writeButton) return;
      event.preventDefault();
      writeButton.click();
      section.querySelector('#judgeme_product_reviews')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  function initBrideCart() {
    const drawer = document.querySelector('#site-cart-sidebar.bride-cart');
    if (!drawer || drawer.dataset.brideReady) return;
    drawer.dataset.brideReady = 'true';
    const syncDrawerCount = () => {
      const count = document.querySelector('#AjaxCartForm [data-cart-count]')?.textContent?.trim() || '0';
      drawer.querySelector('[data-bride-cart-count]').textContent = count;
      const checkout = drawer.querySelector('#CheckOut');
      if (checkout) checkout.disabled = Number(count) === 0;
    };
    document.getElementById('AjaxCartForm')?.addEventListener('cart-updated', syncDrawerCount);
    syncDrawerCount();
    drawer.querySelector('[data-bride-cart-continue]')?.addEventListener('click', () => drawer.hide());
    document.getElementById('cart-open-button')?.addEventListener('click', () => {
      const banner = drawer.querySelector('[data-bride-cart-added]');
      if (banner) banner.hidden = true;
    });
    const recommendations = drawer.querySelector('[data-bride-cart-recommendations]');
    const cards = [...document.querySelectorAll('.bride-complete-look [data-bride-look-item]')].slice(0, 3);
    if (!recommendations || !cards.length) return;
    const heading = document.createElement('h3');
    heading.textContent = 'Something blue?';
    recommendations.append(heading);
    cards.forEach(card => {
      const row = document.createElement('div');
      row.className = 'bride-cart__recommendation';
      const image = card.querySelector('img');
      if (image) {
        const thumbnail = document.createElement('img');
        thumbnail.src = image.currentSrc || image.src;
        thumbnail.alt = '';
        thumbnail.loading = 'lazy';
        row.append(thumbnail);
      }
      const name = document.createElement('span');
      name.textContent = `${card.querySelector('h3')?.textContent?.trim() || 'Accessory'} · ${money(card.dataset.price)}`;
      row.append(name);
      const quick = card.querySelector('[data-bride-quick-add]');
      if (quick) {
        const button = document.createElement('button');
        button.type = 'button';
        button.textContent = isPreorder(card) ? 'Pre-order' : '+ Add';
        button.dataset.brideCartRecommendation = card.dataset.variantId;
        button.addEventListener('click', () => quick.click());
        row.append(button);
      } else {
        const link = document.createElement('a');
        link.href = card.querySelector('h3 a')?.href || '#';
        link.textContent = 'View';
        row.append(link);
      }
      recommendations.append(row);
    });
  }

  const init = root => {
    root.querySelectorAll('.bride-suits, [data-bride-quick-products]').forEach(initSuitQuickAdd);
    if (!bridalTemplate) return;
    root.querySelectorAll('[data-bride-product]').forEach(initProduct);
    root.querySelectorAll('.bride-complete-look').forEach(initLook);
    root.querySelectorAll('[data-bride-video-dialog]').forEach(initVideo);
    root.querySelectorAll('.bride-reviews').forEach(initReviews);
    initBrideCart();
  };
  init(document);
  document.addEventListener('shopify:section:load', event => init(event.target));
})();
